import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  Tag,
  Plus,
  Copy as CopyIcon,
  Check,
  Pencil,
  Trash2,
  Power,
  AlertCircle,
  ExternalLink,
} from "lucide-react";

import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../../../components/ui/Table";
import Button from "../../../components/ui/Button";
import { Spinner } from "../../../components/ui/Spinner";

import {
  listSignupReferences,
  deleteSignupReference,
  type SignupReferenceView,
} from "../../../api/General/Subscription";
import SignupReferenceDialog from "./SignupReferenceDialog";

/**
 * Operator-facing catalog of signup attribution slugs. Each row resolves to
 * a partner-specific signup URL (`<ClientUrl>/signup?ref=<slug>`) that can be
 * copied + handed to distributors, and to a count of companies signed up via
 * that slug.
 */
export default function SignupReferencesPanel() {
  const { t } = useTranslation();

  const [rows, setRows] = useState<SignupReferenceView[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<SignupReferenceView | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const res = await listSignupReferences();
      if (res.errors && res.errors.length > 0) {
        setErrorMsg(res.errors[0]?.message || t("Failed to load references"));
        setRows(null);
      } else {
        setRows(res.payload ?? []);
      }
    } catch {
      setErrorMsg(t("Failed to load references"));
      setRows(null);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const openAdd = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (row: SignupReferenceView) => {
    setEditing(row);
    setDialogOpen(true);
  };

  const copyUrl = async (row: SignupReferenceView) => {
    try {
      await navigator.clipboard.writeText(row.signupUrl);
      setCopiedId(row.id);
      window.setTimeout(() => setCopiedId((cur) => (cur === row.id ? null : cur)), 1500);
      toast.success(t("Signup URL copied"));
    } catch {
      toast.error(t("Couldn't copy — select the URL manually."));
    }
  };

  const onDelete = async (row: SignupReferenceView) => {
    const message =
      row.companyCount > 0
        ? t("This reference has {{count}} companies attributed to it. Deactivating it preserves history but stops new attributions.", {
            count: row.companyCount,
          })
        : t("Delete this reference?");
    if (!window.confirm(message)) return;
    try {
      const res = await deleteSignupReference(row.id);
      if (res.errors && res.errors.length > 0) {
        toast.error(res.errors[0]?.message || t("Failed to remove reference"));
        return;
      }
      toast.success(row.companyCount > 0 ? t("Reference deactivated") : t("Reference removed"));
      load();
    } catch {
      toast.error(t("Failed to remove reference"));
    }
  };

  const isEmpty = rows != null && rows.length === 0;

  return (
    <div className="rounded-xl border border-border bg-card">
      <div className="flex items-center gap-3 p-4 border-b border-border">
        <div className="shrink-0 w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
          <Tag size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold text-foreground">{t("Signup references")}</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t("Manage referral slugs and copy partner-specific signup URLs.")}
          </p>
        </div>
        <Button onClick={openAdd} size="sm">
          <Plus size={14} className="me-1.5" />
          {t("Add reference")}
        </Button>
      </div>

      {errorMsg && (
        <div className="p-4">
          <div className="flex items-start gap-2 rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 p-3 text-sm text-red-800 dark:text-red-300">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span className="flex-1">{errorMsg}</span>
            <Button variant="outline" size="sm" onClick={load}>
              {t("Retry")}
            </Button>
          </div>
        </div>
      )}

      {!errorMsg && loading && (
        <div className="py-16 flex items-center justify-center">
          <Spinner size="md" />
        </div>
      )}

      {!errorMsg && !loading && isEmpty && (
        <div className="py-16 px-4 text-center space-y-2">
          <div className="mx-auto w-12 h-12 rounded-full bg-muted/60 flex items-center justify-center text-muted-foreground">
            <Tag size={20} />
          </div>
          <div className="text-sm font-medium text-foreground">
            {t("No references yet")}
          </div>
          <p className="text-xs text-muted-foreground max-w-sm mx-auto">
            {t("Create a reference to track which partner or campaign drives self-serve signups.")}
          </p>
          <Button onClick={openAdd} size="sm" className="mt-2">
            <Plus size={14} className="me-1.5" />
            {t("Add reference")}
          </Button>
        </div>
      )}

      {!errorMsg && !loading && !isEmpty && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="ps-4">{t("Slug")}</TableHead>
              <TableHead>{t("Name")}</TableHead>
              <TableHead className="text-end">{t("Signups")}</TableHead>
              <TableHead>{t("Status")}</TableHead>
              <TableHead>{t("Signup URL")}</TableHead>
              <TableHead className="text-end pe-4">
                <span className="sr-only">{t("Actions")}</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows!.map((row) => (
              <TableRow key={row.id}>
                <TableCell className="ps-4 font-mono text-xs">{row.slug}</TableCell>
                <TableCell>
                  <div className="font-medium text-foreground">{row.name}</div>
                  {row.description && (
                    <div className="text-xs text-muted-foreground mt-0.5 max-w-[40ch] truncate">
                      {row.description}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-end tabular-nums">{row.companyCount}</TableCell>
                <TableCell>
                  {row.isActive ? (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-900/50">
                      <Power size={10} />
                      {t("Active")}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium bg-muted text-muted-foreground border border-border">
                      {t("Inactive")}
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    <code className="text-xs text-muted-foreground truncate max-w-[28ch]" title={row.signupUrl}>
                      {row.signupUrl}
                    </code>
                    <button
                      type="button"
                      onClick={() => copyUrl(row)}
                      className="p-1 rounded text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                      aria-label={t("Copy signup URL")}
                    >
                      {copiedId === row.id ? <Check size={14} className="text-emerald-600" /> : <CopyIcon size={14} />}
                    </button>
                    <a
                      href={row.signupUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1 rounded text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                      aria-label={t("Open signup URL")}
                    >
                      <ExternalLink size={14} />
                    </a>
                  </div>
                </TableCell>
                <TableCell className="text-end pe-4">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      type="button"
                      onClick={() => openEdit(row)}
                      className="p-1.5 rounded text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                      aria-label={t("Edit")}
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete(row)}
                      className="p-1.5 rounded text-muted-foreground hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950/30 dark:hover:text-red-300 transition-colors"
                      aria-label={row.companyCount > 0 ? t("Deactivate") : t("Remove")}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <SignupReferenceDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        reference={editing}
        onSuccess={() => {
          toast.success(editing ? t("Reference updated") : t("Reference created"));
          load();
        }}
      />
    </div>
  );
}
